// WAAPI motion helper. Respects prefers-reduced-motion by SNAPPING to the end state
// (the state change still happens — motion is additive, never load-bearing; R4.5/R8.4).
//
// CSP-safe: WAAPI animates element properties at runtime via the Web Animations API; it does
// NOT inject inline <style> or style attributes, so it is unaffected by `style-src 'self'`.

let _reducedOverride: boolean | null = null;

/** Allow tests/QA to force reduced-motion on/off. */
export function setReducedMotion(value: boolean | null): void {
  _reducedOverride = value;
}

export function prefersReducedMotion(): boolean {
  if (_reducedOverride !== null) return _reducedOverride;
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface AnimateOpts extends KeyframeAnimationOptions {
  /** When true, run even under reduced-motion (use only for essential, gentle motion). */
  force?: boolean;
}

/**
 * Animate an element with WAAPI. Under reduced-motion (and not forced) it jumps straight to
 * the final keyframe and resolves immediately, so comprehension is preserved without motion.
 */
export function animate(
  el: Element,
  keyframes: Keyframe[],
  opts: AnimateOpts = {},
): Animation | null {
  const { force, ...waapi } = opts;
  if (prefersReducedMotion() && !force) {
    // Apply the final keyframe as a 0-duration fill so the visual end-state is reached.
    const last = keyframes[keyframes.length - 1];
    const anim = el.animate([last, last], { duration: 0.001, fill: 'forwards' });
    return anim;
  }
  return el.animate(keyframes, {
    duration: 240,
    easing: 'cubic-bezier(0.2, 0, 0, 1)',
    fill: 'both',
    ...waapi,
  });
}

/** Await an animation's finish (resolves immediately if null/reduced). */
export function whenDone(anim: Animation | null): Promise<void> {
  if (!anim) return Promise.resolve();
  return anim.finished.then(() => undefined).catch(() => undefined);
}

/** Promise-based delay that respects reduced-motion (near-instant when reduced). */
export function pause(ms: number): Promise<void> {
  const d = prefersReducedMotion() ? 0 : ms;
  return new Promise((r) => setTimeout(r, d));
}
